import { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Switch,
  ToggleGroup,
  ToggleGroupItem,
} from '@design-system';
import { possessive } from '@shared';
import { usePolicyDetails } from './use-policy-details';
import type { GenworthPolicyDetails, InflationType } from './policy-details-storage';

interface PolicyDetailsDialogProps {
  client: Client;
  open: boolean;
  onClose: () => void;
}

type FormState = {
  monthlyPremium: string;
  benefitPool: string;
  dailyBenefitMax: string;
  monthlyBenefitMax: string;
  inflationType: InflationType;
  inflationRate: string;
  waitingPeriodDays: string;
  isPartnership: boolean;
  originalPremium: string;
};

function numToStr(value: number | undefined) {
  return value === undefined ? '' : String(value);
}

function strToNum(value: string) {
  const trimmed = value.trim();

  if (trimmed === '') return undefined;

  const parsed = Number(trimmed);

  return Number.isFinite(parsed) ? parsed : undefined;
}

function toFormState(details: GenworthPolicyDetails): FormState {
  return {
    monthlyPremium: numToStr(details.monthlyPremium),
    benefitPool: numToStr(details.benefitPool),
    dailyBenefitMax: numToStr(details.dailyBenefitMax),
    monthlyBenefitMax: numToStr(details.monthlyBenefitMax),
    inflationType: details.inflationType ?? 'compound',
    inflationRate: numToStr(details.inflationRate),
    waitingPeriodDays: numToStr(details.waitingPeriodDays),
    isPartnership: details.isPartnership ?? false,
    originalPremium: numToStr(details.originalPremium),
  };
}

export function PolicyDetailsDialog({ client, open, onClose }: PolicyDetailsDialogProps) {
  const { details, save } = usePolicyDetails(client.clientId);
  const [form, setForm] = useState<FormState>(() => toFormState(details));

  useEffect(() => {
    if (open) setForm(toFormState(details));
  }, [open, details]);

  const setField = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm(prev => ({ ...prev, [key]: value }));

  const handleSave = () => {
    save({
      monthlyPremium: strToNum(form.monthlyPremium),
      benefitPool: strToNum(form.benefitPool),
      dailyBenefitMax: strToNum(form.dailyBenefitMax),
      monthlyBenefitMax: strToNum(form.monthlyBenefitMax),
      inflationType: form.inflationType,
      inflationRate: form.inflationType === 'none' ? undefined : strToNum(form.inflationRate),
      waitingPeriodDays: strToNum(form.waitingPeriodDays),
      isPartnership: form.isPartnership,
      originalPremium: strToNum(form.originalPremium),
    });
    onClose();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={isOpen => !isOpen && onClose()}
    >
      <DialogContent className="max-h-[85vh] w-full max-w-lg overflow-y-auto">
        <DialogHeader className="text-start">
          <DialogTitle>{possessive(client.clientFirstName)} Policy Details</DialogTitle>
          <DialogDescription>
            Enter the figures from this client's policy. They'll drive the policy-education
            walkthrough. Saved on this device only.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-6 py-2">
          <div className="grid grid-cols-2 gap-3">
            <CurrencyField
              id="monthlyPremium"
              label="Monthly premium"
              value={form.monthlyPremium}
              onChange={value => setField('monthlyPremium', value)}
            />

            <CurrencyField
              id="benefitPool"
              label="Total benefit pool"
              value={form.benefitPool}
              onChange={value => setField('benefitPool', value)}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <CurrencyField
              id="dailyBenefitMax"
              label="Daily benefit max"
              value={form.dailyBenefitMax}
              onChange={value => setField('dailyBenefitMax', value)}
            />
            <CurrencyField
              id="monthlyBenefitMax"
              label="Monthly benefit max"
              value={form.monthlyBenefitMax}
              onChange={value => setField('monthlyBenefitMax', value)}
            />
          </div>

          <div className="grid gap-2">
            <Label>Inflation protection</Label>
            <ToggleGroup
              type="single"
              variant="outline"
              value={form.inflationType}
              onValueChange={value => value && setField('inflationType', value as InflationType)}
              className="grid w-full grid-cols-3"
            >
              <ToggleGroupItem
                value="compound"
                className="w-full"
              >
                Compound
              </ToggleGroupItem>
              <ToggleGroupItem
                value="simple"
                className="w-full"
              >
                Simple
              </ToggleGroupItem>
              <ToggleGroupItem
                value="none"
                className="w-full"
              >
                None
              </ToggleGroupItem>
            </ToggleGroup>
            {form.inflationType !== 'none' && (
              <div className="relative mt-2 w-full">
                <Input
                  inputMode="decimal"
                  placeholder="Rate"
                  value={form.inflationRate}
                  onChange={event => setField('inflationRate', event.target.value)}
                  className="pr-7"
                />
                <span className="text-muted-foreground pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm">
                  %
                </span>
              </div>
            )}
          </div>

          <div className="grid gap-2">
            <Label htmlFor="waitingPeriodDays">Waiting period</Label>
            <div className="relative">
              <Input
                id="waitingPeriodDays"
                inputMode="numeric"
                placeholder="0"
                value={form.waitingPeriodDays}
                onChange={event => setField('waitingPeriodDays', event.target.value)}
                className="pr-14"
              />
              <span className="text-muted-foreground pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm">
                days
              </span>
            </div>
          </div>

          <CurrencyField
            id="originalPremium"
            label="Original monthly premium"
            hint="Used to show how the premium has changed over time."
            value={form.originalPremium}
            onChange={value => setField('originalPremium', value)}
          />

          <div className="flex items-center justify-between rounded-lg border p-3">
            <div>
              <Label
                htmlFor="isPartnership"
                className="cursor-pointer"
              >
                Partnership policy
              </Label>
              <p className="text-muted-foreground text-xs">Part of a state Partnership Program.</p>
            </div>
            <Switch
              id="isPartnership"
              checked={form.isPartnership}
              onCheckedChange={checked => setField('isPartnership', checked)}
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button onClick={handleSave}>Save Policy Details</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CurrencyField({
  id,
  label,
  value,
  hint,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  hint?: string;
  onChange: (value: string) => void;
}) {
  return (
    <div className="grid gap-2">
      <Label htmlFor={id}>{label}</Label>
      <div className="relative">
        <span className="text-muted-foreground pointer-events-none absolute inset-y-0 left-3 flex items-center text-sm">
          $
        </span>
        <Input
          id={id}
          inputMode="decimal"
          placeholder="0"
          value={value}
          onChange={event => onChange(event.target.value)}
          className="pl-7"
        />
      </div>
      {hint && <p className="text-muted-foreground text-xs">{hint}</p>}
    </div>
  );
}
